import type { ReactNode } from 'react'

import type { IncidentStatus, Severity } from '../api/types'

const SEVERITY_STYLES: Record<Severity, string> = {
  critical: 'bg-severity-critical/15 text-severity-critical border-severity-critical/40',
  high: 'bg-severity-high/15 text-severity-high border-severity-high/40',
  warning: 'bg-severity-warning/15 text-severity-warning border-severity-warning/40',
  info: 'bg-severity-info/15 text-severity-info border-severity-info/40',
}

const STATUS_STYLES: Record<IncidentStatus, string> = {
  firing: 'bg-red-500/15 text-red-300 border-red-500/40',
  escalated: 'bg-orange-500/15 text-orange-300 border-orange-500/40',
  acknowledged: 'bg-sky-500/15 text-sky-300 border-sky-500/40',
  silenced: 'bg-slate-500/15 text-slate-300 border-slate-500/40',
  suppressed: 'bg-slate-500/15 text-slate-400 border-slate-500/40',
  resolved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
}

const STATUS_LABELS: Record<IncidentStatus, string> = {
  firing: 'горит',
  escalated: 'эскалация',
  acknowledged: 'принят',
  silenced: 'заглушен',
  suppressed: 'подавлен',
  resolved: 'закрыт',
}

export function SeverityBadge({ severity }: { severity: Severity }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded border text-xs font-semibold uppercase ${SEVERITY_STYLES[severity]}`}
    >
      {severity}
    </span>
  )
}

export function StatusBadge({ status }: { status: IncidentStatus }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${STATUS_STYLES[status]}`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  )
}

export function Labels({ labels, limit = 6 }: { labels: Record<string, string>; limit?: number }) {
  const entries = Object.entries(labels ?? {})
  const shown = entries.slice(0, limit)
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map(([key, value]) => (
        <span
          key={key}
          className="font-mono text-[11px] bg-ink-700 border border-ink-600 rounded px-1.5 py-0.5 text-slate-300"
        >
          {key}={value}
        </span>
      ))}
      {entries.length > shown.length && (
        <span className="text-[11px] text-slate-500">+{entries.length - shown.length}</span>
      )}
    </div>
  )
}

export function Card({
  title,
  actions,
  children,
  className = '',
}: {
  title?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        // flex-wrap: на узком экране кнопки уходят на вторую строку,
        // а не выдавливают заголовок за край карточки.
        <header className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-ink-600">
          <h2 className="text-sm font-semibold text-slate-200">{title}</h2>
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        </header>
      )}
      {children}
    </section>
  )
}

export function Stat({
  label,
  value,
  tone = 'default',
}: {
  label: string
  value: ReactNode
  tone?: 'default' | 'critical' | 'good'
}) {
  const toneClass =
    tone === 'critical' ? 'text-severity-critical' : tone === 'good' ? 'text-emerald-400' : 'text-slate-100'
  return (
    <div className="card px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${toneClass}`}>{value}</div>
    </div>
  )
}

/**
 * Таблица со своей горизонтальной прокруткой.
 *
 * Шесть колонок в 360 пикселей не помещаются никаким переносом. Вместо того
 * чтобы ломать вёрстку страницы, таблица прокручивается внутри своей рамки:
 * `min-w` держит колонки читаемыми, а прокрутка остаётся локальной — сама
 * страница по горизонтали не едет.
 */
export function TableScroll({
  children,
  minWidth = 640,
}: {
  children: ReactNode
  minWidth?: number
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full" style={{ minWidth: `${minWidth}px` }}>
        {children}
      </table>
    </div>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="px-4 py-10 text-center text-sm text-slate-500">{children}</div>
}

export function ErrorNote({ children }: { children: ReactNode }) {
  if (!children) return null
  return (
    <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
      {children}
    </div>
  )
}

export function Spinner({ label = 'Загрузка…' }: { label?: string }) {
  return <div className="px-4 py-10 text-center text-sm text-slate-500">{label}</div>
}

/** Человекочитаемая длительность: SLA-показатели и возраст инцидента. */
export function humanDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'
  const total = Math.max(0, Math.round(seconds))
  if (total < 60) return `${total} с`
  const minutes = Math.floor(total / 60)
  if (minutes < 60) return `${minutes} мин`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} ч ${minutes % 60} мин`
  return `${Math.floor(hours / 24)} д ${hours % 24} ч`
}

export function since(iso: string | null | undefined): string {
  if (!iso) return '—'
  return humanDuration((Date.now() - new Date(iso).getTime()) / 1000)
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'medium' })
}
